import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  ArrowRight,
  Clock,
  DollarSign,
  ListChecks,
  ListPlus,
  Pencil,
  PieChart,
  Receipt,
  Target,
  Trash2,
  TriangleAlert,
  UserPlus,
  Users,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  BackLink,
  Button,
  buttonVariants,
  burnTitle,
  isPartialBurn,
  Card,
  CardHeader,
  CardRow,
  ChartFrame,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  ProgressBar,
  Select,
  Skeleton,
  SkeletonTable,
  StatCard,
  StatIcon,
  StatusPill,
  type StatKind,
  Tabs,
  useReturnOrigin,
  withReturn,
  type DataTableColumn,
} from '../../components/ui';
import { ApiError } from '../../lib/api';
import { cn } from '../../lib/cn';
import {
  formatDate,
  formatHours,
  formatMoney,
  formatMoneyCompact,
  formatMonth,
  formatMonthShort,
  formatPercent,
} from '../../lib/format';
import {
  useAddAssignment,
  useArchiveAgreement,
  useAttachStandardTasks,
  useAttachTask,
  useDetachTask,
  useAgreementDetail,
  useRemoveAssignment,
  useUpdateAssignment,
  useUpdateAgreementTask,
  useUpdateTask,
  type Assignment,
  type AgreementDetail,
  type AgreementExpense,
  type AgreementTaskRow,
  type TaskBreakdown,
} from './api';
import {
  BASIS_LABEL,
  MarginText,
  RateCell,
  REVENUE_SOURCE_NOUN,
  SignedMoney,
  TypeBadge,
  VerdictChip,
} from './components';
import { AgreementHubLink } from './AgreementHubLink';
import { AgreementModal } from './AgreementModal';
import { SellHoursModal } from './SellHoursModal';
import { PrepaidHoursCard } from './PrepaidHoursCard';
import { AgreementTimeTab, timeTabSearch } from './AgreementTimeTab';
import { useAgreementReports } from '../hours-report/api';
import { partialPeriod, agreementAction, agreementVerdict, type AgreementActionKind } from './verdict';

// ---------------------------------------------------------------------------
// Chart helpers
// ---------------------------------------------------------------------------

const NICE_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25, 30, 40, 50, 60, 80, 100];

/**
 * Axis scale: round ticks AND a top that hugs the data.
 *
 * The old version padded by 5% and then pushed one more tick past the result,
 * so 1,465 h drew a 0–2,000 axis and $42K drew a 0–$60K axis — a third to a
 * half of every chart was empty. This picks, from a ladder of round steps, the
 * one whose top tick is closest above the data while keeping 3–6 gridlines.
 */
export function axisScale(max: number): { max: number; ticks: number[] } {
  if (!(max > 0) || !Number.isFinite(max)) return { max: 1, ticks: [0, 1] };
  const mag = Math.pow(10, Math.floor(Math.log10(max)) - 1);
  let best: { step: number; top: number } | null = null;
  for (const f of NICE_STEPS) {
    const step = f * mag;
    const n = Math.ceil(max / step - 1e-9);
    if (n < 3 || n > 6) continue;
    const top = n * step;
    if (!best || top < best.top) best = { step, top };
  }
  // Degenerate inputs only (e.g. max smaller than the smallest ladder step).
  if (!best) best = { step: max / 4, top: max };
  const ticks: number[] = [];
  for (let v = 0; v <= best.top + best.step / 1000; v += best.step) {
    ticks.push(Number(v.toFixed(6)));
  }
  return { max: best.top, ticks };
}

/**
 * x labels at a CONSTANT month stride, anchored on the last data point.
 *
 * The previous version rounded a fractional step, which produced Nov 2025, Jan,
 * Mar, Apr, Jun, Aug — gaps of 2,2,1,2,2. On a cumulative chart an axis whose
 * ticks are unevenly spaced makes the slope lie about the rate of burn: the
 * same monthly pace draws a steeper segment wherever the ticks are closer
 * together. Ticks are now strictly every `stride` months, so equal horizontal
 * distance always means equal elapsed time. The final month is always a tick
 * (Recharts' own thinning used to drop it).
 */
export function pickXTicks(labels: string[], target = 6): string[] {
  if (labels.length <= target) return labels;
  const stride = Math.ceil((labels.length - 1) / (target - 1));
  const idx: number[] = [];
  for (let i = labels.length - 1; i >= 0; i -= stride) idx.push(i);
  return idx.reverse().map((i) => labels[i]);
}

/** Month key + n → 'YYYY-MM' */
function addMonths(month: string, n: number): string {
  const [y, m] = month.split('-').map(Number);
  const total = y * 12 + (m - 1) + n;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

interface ChartPoint {
  month: string;
  label: string;
  value: number;
  compare?: number;
  /** Dashed accrual/contract line (monthly fee × months elapsed). */
  reference?: number;
  /**
   * The gap between revenue and cost, drawn as a shaded band. `gain` when
   * revenue is on top (that band IS the margin), `loss` when cost is on top.
   * Exactly one is set per point; the other is undefined so Recharts leaves it
   * unpainted.
   */
  gain?: [number, number];
  loss?: [number, number];
  monthDelta: number;
  // Cumulative to-date figures for the tooltip. A cost/margin product whose
  // hover only reads out hours is answering a question nobody asked.
  hoursToDate: number;
  costToDate: number;
  billableToDate: number;
  invoicedToDate: number;
}

interface ChartConfig {
  title: string;
  subtitle: string;
  unit: 'hours' | 'money';
  points: ChartPoint[];
  /** Flat reference line (fixed budget/fee). */
  budget: number | null;
  seriesName: string;
  compareName?: string;
  referenceName?: string;
  /**
   * Revenue-vs-cost mode: draw both series as lines of equal weight and shade
   * the region between them (green = margin, red = loss).
   *
   * Cost used to be the confident blue line under a soft blue gradient while
   * revenue was a thin green thread pinned to the floor — the catastrophic
   * quantity rendered as the calm, pretty, dominant shape. And the subtitle
   * promised "the gap is the margin" without ever drawing the gap.
   */
  gap?: boolean;
  /** In gap mode: true when `value` is the cost series and `compare` the revenue. */
  costIsPrimary?: boolean;
  /** Label of the month where the series first crossed the budget. */
  crossedAt: string | null;
  /** Projected exhaustion, when the budget has NOT been crossed yet. */
  projection: string | null;
  /** Set when the agreement is finished — suppresses any forward-looking copy. */
  endNote: string | null;
}

/**
 * The chart answers the question the agreement's basis actually poses:
 *   hourly     → are we burning the client's hours/budget faster than planned?
 *   fee-based  → is delivery cost eating the fee? (cost vs fee, never "billable
 *                value vs fee" — an hourly valuation of fixed-fee work is not a
 *                number anyone is owed, and plotting it against a $250 line
 *                makes the line invisible AND meaningless.)
 */
function chartConfig(detail: AgreementDetail): ChartConfig {
  const { metrics, byMonth, agreement } = detail;
  const label = (m: string) => formatMonth(`${m}-01`);

  // A finished agreement gets no forward-looking copy. "At the last 3 months'
  // pace the budget is exhausted around May 2026" printed on a microsite that
  // shipped in January is a projection into a past that already happened.
  const today = new Date().toISOString().slice(0, 10);
  const ended = agreement.endDate != null && agreement.endDate < today;
  const closed = ended || !agreement.isActive;

  const base = {
    crossedAt: null as string | null,
    projection: null as string | null,
    endNote: null as string | null,
  };

  /** Cumulative to-date figures every tooltip can read out. */
  const readout = (m: (typeof byMonth)[number]) => ({
    hoursToDate: m.cumulativeHours,
    costToDate: m.cumulativeCost,
    billableToDate: m.cumulativeBillable,
    invoicedToDate: m.cumulativeInvoiced,
  });

  /** The shaded region between revenue and cost at one month. */
  const band = (revenue: number, cost: number) =>
    revenue >= cost
      ? { gain: [cost, revenue] as [number, number] }
      : { loss: [revenue, cost] as [number, number] };

  const withCrossing = (cfg: ChartConfig): ChartConfig => {
    if (cfg.points.length === 0) return cfg;
    const last = cfg.points[cfg.points.length - 1];
    const finalNote = (): string | null => {
      if (!closed) return null;
      const when = ended && agreement.endDate ? `Ended ${formatDate(agreement.endDate)}` : 'Archived';
      if (cfg.budget != null && cfg.budget > 0) {
        return `${when} — finished at ${Math.round((last.value / cfg.budget) * 100)}% of budget.`;
      }
      return `${when} — this chart is final.`;
    };
    if (cfg.budget == null || cfg.budget <= 0) return { ...cfg, endNote: finalNote() };
    const crossIdx = cfg.points.findIndex((p) => p.value >= cfg.budget!);
    if (crossIdx >= 0) {
      return { ...cfg, crossedAt: cfg.points[crossIdx].label, endNote: closed ? finalNote() : null };
    }
    if (closed) return { ...cfg, endNote: finalNote() };
    // Live and not crossed: project forward on the last 3 months' pace.
    const tail = cfg.points.slice(-3);
    const pace = tail.length > 0 ? tail.reduce((s, p) => s + p.monthDelta, 0) / tail.length : 0;
    if (pace <= 0) return cfg;
    const monthsLeft = Math.ceil((cfg.budget - last.value) / pace);
    if (monthsLeft > 60) return cfg;
    return { ...cfg, projection: formatMonth(`${addMonths(last.month, monthsLeft)}-01`) };
  };

  // --- fee-based: cumulative cost vs the revenue the margin is struck on ----
  //
  // MONTHLY_FEE recognises what it has INVOICED — a recurring fee is earned as
  // it is billed — so the green line is the exact figure the Margin KPI
  // divides by and "the gap is the margin" is literally true.
  //
  // FIXED_BID recognises the CONTRACT: the price was agreed once, and hours are
  // cost against it. So the shaded gap there is measured from the fee line, not
  // from the billing progress — otherwise a half-invoiced build would shade red
  // beside a healthy margin. Invoiced stays on the chart as billing progress,
  // which is a different (and still useful) question.
  if (metrics.basis === 'MONTHLY_FEE' || metrics.basis === 'FIXED_FEE') {
    const fee = agreement.budgetAmount ?? 0;
    const monthly = metrics.basis === 'MONTHLY_FEE';
    const onContract = metrics.revenueSource === 'CONTRACT';
    return withCrossing({
      ...base,
      title: monthly ? 'Cost against revenue' : 'Cost against the contract',
      subtitle: monthly
        ? `Cumulative delivery cost vs revenue invoiced — the gap is the margin. The dashed line is the ${formatMoney(fee, { whole: true })}/month fee accruing.`
        : onContract
          ? `Cumulative delivery cost against the ${formatMoney(fee, { whole: true })} contract value — the space under the fee line is the margin. The second line is how much of it has been invoiced so far.`
          : `Cumulative delivery cost and revenue invoiced — no contract value is set on this agreement, so the margin follows what has been invoiced`,
      unit: 'money',
      budget: monthly ? null : fee || null,
      seriesName: 'Cost',
      compareName: 'Invoiced',
      referenceName: monthly ? 'Fee accrued' : undefined,
      gap: true,
      costIsPrimary: true,
      points: byMonth.map((m, i) => ({
        month: m.month,
        label: label(m.month),
        value: m.cumulativeCost,
        compare: m.cumulativeInvoiced,
        reference: monthly ? fee * (i + 1) : undefined,
        monthDelta: m.cost,
        ...band(onContract ? fee : m.cumulativeInvoiced, m.cumulativeCost),
        ...readout(m),
      })),
    });
  }

  // --- hours budget --------------------------------------------------------
  if (metrics.burn?.basis === 'HOURS') {
    return withCrossing({
      ...base,
      title: 'Budget burn',
      subtitle: `Cumulative hours against the ${formatHours(agreement.budgetHours ?? 0)}-hour budget`,
      unit: 'hours',
      budget: agreement.budgetHours,
      seriesName: 'Hours',
      points: byMonth.map((m) => ({
        month: m.month,
        label: label(m.month),
        value: m.cumulativeHours,
        monthDelta: m.hours,
        ...readout(m),
      })),
    });
  }

  // --- hourly, amount budget or none --------------------------------------
  //
  // An hourly agreement with no hours budget used to plot billable value ALONE —
  // the weakest of the three modes, and the one every Support agreement got, i.e.
  // precisely where the cost/margin differentiator earns its keep. It now plots
  // billable value against delivery cost with the margin shaded between them.
  if (metrics.basis === 'HOURLY') {
    const hasBudget = metrics.burn?.basis === 'AMOUNT';
    return withCrossing({
      ...base,
      title: 'Billable value against cost',
      subtitle: hasBudget
        ? `Cumulative billable value against the ${formatMoney(agreement.budgetAmount ?? 0, { whole: true })} budget, with delivery cost underneath — the gap is the margin`
        : 'Cumulative billable value and what it cost to deliver — the shaded gap is the margin',
      unit: 'money',
      budget: hasBudget ? agreement.budgetAmount : null,
      seriesName: 'Billable value',
      compareName: 'Cost',
      gap: true,
      points: byMonth.map((m) => ({
        month: m.month,
        label: label(m.month),
        value: m.cumulativeBillable,
        compare: m.cumulativeCost,
        monthDelta: m.billableAmount,
        ...band(m.cumulativeBillable, m.cumulativeCost),
        ...readout(m),
      })),
    });
  }

  return withCrossing({
    ...base,
    title: 'Hours over time',
    subtitle: 'Cumulative total since the agreement started',
    unit: 'hours',
    budget: null,
    seriesName: 'Hours',
    points: byMonth.map((m) => ({
      month: m.month,
      label: label(m.month),
      value: m.cumulativeHours,
      monthDelta: m.hours,
      ...readout(m),
    })),
  });
}

const COST_STROKE = '#dc2626';
const REVENUE_STROKE = '#16a34a';

/** Live media query — the chart has to know its width, not guess from CSS. */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/**
 * `minHeight` is a FLOOR, not a height: the plot fills whatever the card's row
 * gives it (app-wide fill rule) and never drops below what stays readable.
 */
function BurnChart({ detail, minHeight = 260 }: { detail: AgreementDetail; minHeight?: number }) {
  /**
   * Below ~600px the chart is ~230px of plot area. Six month labels collided
   * into "Dec 2025Feb 2026Apr 2026Jun 2026Aug 2026" and the two in-plot
   * annotations overprinted each other into unreadable mush. Narrow gets three
   * ticks, one annotation (the budget crossing — the partial month is already
   * stated in the subtitle above), and a tighter gutter.
   */
  const narrow = useMediaQuery('(max-width: 600px)');
  const cfg = chartConfig(detail);
  const fmt = (v: number) =>
    cfg.unit === 'money' ? formatMoney(v, { whole: true }) : `${formatHours(v)} h`;
  const axisFmt = (v: number) => (cfg.unit === 'money' ? formatMoneyCompact(v) : formatHours(v));

  if (cfg.points.length === 0) {
    return (
      <EmptyState
        icon={<Clock />}
        title="No time tracked yet"
        description="The burn chart appears once time entries land on this agreement."
      />
    );
  }

  const dataMax = Math.max(
    ...cfg.points.map((p) => Math.max(p.value, p.compare ?? 0, p.reference ?? 0)),
    cfg.budget ?? 0,
  );
  const { max: axisMax, ticks } = axisScale(dataMax);
  const xTicks = pickXTicks(cfg.points.map((p) => p.label), narrow ? 4 : 6);
  // The current month is 12 days of data on a cumulative chart: the curve
  // flattens and reads as "the team stopped working". Hatch it and say so.
  const partial = partialPeriod(detail.byMonth);
  const partialFrom =
    partial && cfg.points.length > 1 ? cfg.points[cfg.points.length - 2].label : null;
  const partialTo = partial ? cfg.points[cfg.points.length - 1].label : null;
  const note = [
    cfg.crossedAt ? `Budget crossed in ${cfg.crossedAt}.` : null,
    // Never both: a finished agreement gets its ending, not a forecast.
    cfg.endNote ??
      (cfg.projection
        ? `At the last 3 months' pace the budget is exhausted around ${cfg.projection}.`
        : null),
  ]
    .filter(Boolean)
    .join(' ');
  const money = (v: number) => formatMoney(v, { whole: true });
  const primaryStroke = cfg.gap ? (cfg.costIsPrimary ? COST_STROKE : REVENUE_STROKE) : '#2563eb';
  const compareStroke = cfg.costIsPrimary ? REVENUE_STROKE : COST_STROKE;

  return (
    // A flex column so the plot below can be the part that grows — the card
    // this sits in is stretched by whatever shares its row.
    <div className="flex flex-1 flex-col">
      <CardHeader
        title={cfg.title}
        subtitle={
          <>
            {cfg.subtitle.replace(/\.$/, '')}.
            {note !== '' && <span className="ml-1 font-semibold text-secondary">{note}</span>}
            {partial && (
              <span className="ml-1 text-grey-500">
                {partial.label} is partial — {partial.dayOfMonth} of {partial.daysInMonth} days.
              </span>
            )}
          </>
        }
        className="px-0 pt-0"
      />
      <ChartFrame style={{ minHeight }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={cfg.points}
            margin={{ top: 18, right: narrow ? 26 : 40, bottom: 0, left: narrow ? 0 : 4 }}
          >
            <defs>
              <linearGradient id="burnFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2563eb" stopOpacity={0.18} />
                <stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} />
              </linearGradient>
              <pattern
                id="partialMonth"
                width={6}
                height={6}
                patternTransform="rotate(45)"
                patternUnits="userSpaceOnUse"
              >
                <rect width={6} height={6} fill="#f4f4f6" />
                <line x1={0} y1={0} x2={0} y2={6} stroke="#c9c9d1" strokeWidth={1.5} />
              </pattern>
            </defs>
            <CartesianGrid vertical={false} stroke="#eaeaea" />
            {partialFrom && partialTo && (
              // Hatching, not a tint: the trailing period is INCOMPLETE, and a
              // flat final segment must not be read as a stall.
              <ReferenceArea
                x1={partialFrom}
                x2={partialTo}
                fill="url(#partialMonth)"
                fillOpacity={0.9}
                stroke="none"
                // On a phone this label lands on top of "Budget crossed" and
                // both become illegible. The hatching stays; the words are in
                // the subtitle ("Aug 2026 is partial — 12 of 31 days").
                label={
                  narrow
                    ? undefined
                    : {
                        value: `${partial?.label ?? ''} partial`,
                        position: 'insideBottom',
                        fill: '#7a7a86',
                        fontSize: 10,
                        fontWeight: 700,
                        offset: 6,
                      }
                }
              />
            )}
            <XAxis
              dataKey="label"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: narrow ? 10 : 11, fill: '#9898A5' }}
              interval={0}
              ticks={xTicks}
              minTickGap={0}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: '#9898A5' }}
              tickFormatter={axisFmt}
              width={narrow ? 46 : 60}
              ticks={ticks}
              domain={[0, axisMax]}
              label={
                narrow
                  ? undefined
                  : {
                      value: cfg.unit === 'money' ? 'Cumulative $' : 'Cumulative hours',
                      angle: -90,
                      position: 'insideLeft',
                      style: { fontSize: 10, fill: '#9898A5', textAnchor: 'middle' },
                    }
              }
            />
            <Tooltip
              cursor={{ stroke: '#c9d1d9', strokeDasharray: '3 3' }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const p = payload[0].payload as ChartPoint;
                // Everything to date at this month, not just the plotted
                // series. For a product whose pitch is cost and margin, a
                // hover that reads out "953.5 h" and stops is half an answer.
                const readout: Array<[string, string]> = [];
                const plotted = [cfg.seriesName, cfg.compareName];
                if (cfg.unit !== 'hours' || cfg.seriesName !== 'Hours') {
                  readout.push(['Hours', `${formatHours(p.hoursToDate)} h`]);
                }
                if (!plotted.includes('Cost')) readout.push(['Cost', money(p.costToDate)]);
                if (!plotted.includes('Billable value') && p.billableToDate > 0) {
                  readout.push(['Billable', money(p.billableToDate)]);
                }
                if (cfg.compareName == null && p.invoicedToDate > 0) {
                  readout.push(['Invoiced', money(p.invoicedToDate)]);
                }
                if (cfg.gap) {
                  // The gap has a name and a sign — say it, rather than making
                  // the reader subtract two lines by eye.
                  const rev = cfg.costIsPrimary ? (p.compare ?? 0) : p.value;
                  const cost = cfg.costIsPrimary ? p.value : (p.compare ?? 0);
                  readout.push([
                    rev >= cost ? 'Margin' : 'Shortfall',
                    rev > 0
                      ? `${money(Math.abs(rev - cost))} (${formatPercent((rev - cost) / rev, { decimals: 1 })})`
                      : money(Math.abs(rev - cost)),
                  ]);
                }
                return (
                  <div className="rounded-lg border border-grey-200 bg-white px-3 py-2 shadow-elevated">
                    <p className="text-caption font-bold uppercase tracking-wider text-grey-500">
                      {p.label}
                    </p>
                    <p className="num mt-0.5 text-sm font-bold text-secondary">
                      {cfg.seriesName}: {fmt(p.value)}
                    </p>
                    {p.compare != null && (
                      <p className="num text-xs text-grey-600">
                        {cfg.compareName}: {fmt(p.compare)}
                      </p>
                    )}
                    {p.reference != null && cfg.referenceName && (
                      <p className="num text-xs text-grey-500">
                        {cfg.referenceName}: {fmt(p.reference)}
                      </p>
                    )}
                    {p.monthDelta !== p.value && (
                      <p className="num text-xs text-grey-500">+{fmt(p.monthDelta)} this month</p>
                    )}
                    {readout.length > 0 && (
                      <dl className="mt-1.5 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 border-t border-grey-100 pt-1.5 text-xs">
                        {readout.map(([k, v]) => (
                          <div key={k} className="contents">
                            <dt className="text-grey-500">{k} to date</dt>
                            <dd className="num text-right font-semibold text-secondary">{v}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </div>
                );
              }}
            />
            {(cfg.compareName || cfg.referenceName) && (
              // Explicit payload: the two shading bands are geometry, not
              // series, and Recharts would otherwise list "gain" and "loss"
              // beside the real lines.
              <Legend
                verticalAlign="top"
                align={narrow ? 'left' : 'right'}
                height={narrow ? 38 : 24}
                wrapperStyle={{ fontSize: narrow ? 10 : 11, color: '#525252' }}
                payload={[
                  ...(cfg.referenceName
                    ? [{ value: cfg.referenceName, type: 'plainline' as const, color: '#9898A5', payload: { strokeDasharray: '5 4' } }]
                    : []),
                  ...(cfg.compareName
                    ? [{ value: cfg.compareName, type: 'plainline' as const, color: compareStroke, payload: { strokeDasharray: '0' } }]
                    : []),
                  { value: cfg.seriesName, type: 'plainline' as const, color: primaryStroke, payload: { strokeDasharray: '0' } },
                  ...(cfg.gap
                    ? [{ value: 'Margin / shortfall', type: 'rect' as const, color: '#b8b8c2' }]
                    : []),
                ]}
              />
            )}
            {cfg.budget != null && cfg.budget > 0 && (
              <ReferenceLine
                y={cfg.budget}
                stroke="#758a9e"
                strokeDasharray="6 4"
                label={{
                  value: `Budget ${fmt(cfg.budget)}`,
                  position: 'insideTopLeft',
                  fill: '#5a6f83',
                  fontSize: 11,
                  fontWeight: 700,
                }}
              />
            )}
            {cfg.crossedAt && (
              <ReferenceLine
                x={cfg.crossedAt}
                stroke="#dc2626"
                strokeDasharray="4 3"
                label={{
                  value: 'Budget crossed',
                  position: 'insideBottomLeft',
                  fill: '#dc2626',
                  fontSize: 10,
                  fontWeight: 700,
                }}
              />
            )}
            {cfg.referenceName && (
              // The contract accruing, drawn as a quiet dashed line: context for
              // the revenue line, never mistaken for it.
              <Area
                type="monotone"
                dataKey="reference"
                name={cfg.referenceName}
                stroke="#9898A5"
                strokeWidth={1.5}
                strokeDasharray="5 4"
                fill="none"
                dot={false}
                isAnimationActive={false}
              />
            )}
            {cfg.gap && (
              // The gap the subtitle promises, actually drawn. Green where
              // revenue is on top (that band IS the margin), red where cost is.
              // Both are painted UNDER the two lines and excluded from the
              // legend so they read as shading, not as a third series.
              <>
                <Area
                  type="monotone"
                  dataKey="gain"
                  legendType="none"
                  stroke="none"
                  fill={REVENUE_STROKE}
                  fillOpacity={0.16}
                  connectNulls={false}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
                <Area
                  type="monotone"
                  dataKey="loss"
                  legendType="none"
                  stroke="none"
                  fill={COST_STROKE}
                  fillOpacity={0.16}
                  connectNulls={false}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
              </>
            )}
            {cfg.compareName && (
              <Area
                type="monotone"
                dataKey="compare"
                name={cfg.compareName}
                stroke={compareStroke}
                strokeWidth={2}
                fill="none"
                dot={false}
                isAnimationActive={false}
              />
            )}
            <Area
              type="monotone"
              dataKey="value"
              name={cfg.seriesName}
              stroke={primaryStroke}
              strokeWidth={2}
              // In revenue-vs-cost mode neither series gets a gradient: the
              // shaded gap carries the meaning, and a filled cost curve would
              // make the expensive shape the pretty one.
              fill={cfg.gap ? 'none' : 'url(#burnFill)'}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: '#fff' }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </ChartFrame>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Team tab
// ---------------------------------------------------------------------------

function TeamTab({ detail }: { detail: AgreementDetail }) {
  const agreementId = detail.agreement.id;
  const addAssignment = useAddAssignment(agreementId);
  const updateAssignment = useUpdateAssignment(agreementId);
  const removeAssignment = useRemoveAssignment(agreementId);
  const [personId, setPersonId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Update failed — try again.');
    }
  };

  /** Hours × rate is this agreement's revenue only when it is billed hourly. */
  const hourly = detail.metrics.basis === 'HOURLY';

  const totals = detail.assignments.reduce(
    (t, a) => ({
      hours: t.hours + a.hours,
      billable: t.billable + a.billableAmount,
      cost: t.cost + a.cost,
    }),
    { hours: 0, billable: 0, cost: 0 },
  );

  const columns: Array<DataTableColumn<Assignment>> = [
    {
      key: 'person',
      header: 'Person',
      sortable: true,
      sortValue: (a) => a.person.name,
      render: (a) => (
        <div className="min-w-[10rem]">
          <div className="font-semibold text-secondary">{a.person.name}</div>
          <div className="mt-0.5 text-xs text-grey-500">
            {a.person.type === 'CONTRACTOR' ? 'Contractor' : 'Staff'}
            {a.person.costModel === 'FLAT_MONTHLY' ? ' · salaried' : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'hours',
      header: 'Hours',
      align: 'right',
      sortable: true,
      sortValue: (a) => a.hours,
      render: (a) => formatHours(a.hours, { fixed: true }),
    },
    // "Billable" is hours × rate, which is REVENUE only on an hourly agreement.
    // On a fixed-bid or fee engagement the client agreed a price and nobody
    // will ever be charged this figure — printing it here is what put a large
    // billable number beside a margin struck on the contract. The column is
    // dropped rather than renamed: on those agreements the useful number about a
    // person is what they cost.
    ...(hourly
      ? [
          {
            key: 'billable',
            header: 'Billable',
            align: 'right' as const,
            sortable: true,
            sortValue: (a: Assignment) => a.billableAmount,
            render: (a: Assignment) => formatMoney(a.billableAmount, { whole: true }),
          },
        ]
      : []),
    {
      key: 'cost',
      header: 'Cost',
      align: 'right',
      sortable: true,
      sortValue: (a) => a.cost,
      render: (a) => formatMoney(a.cost, { whole: true }),
    },
    // BILLABLE and COST were already here, one subtraction away from "who is
    // your least profitable person on this agreement" — the exact insight Harvest
    // cannot produce. Only meaningful where billable value IS the revenue.
    ...(hourly
      ? [
          {
            key: 'margin',
            header: 'Margin',
            align: 'right' as const,
            sortable: true,
            sortValue: (a: Assignment) =>
              a.billableAmount > 0 ? (a.billableAmount - a.cost) / a.billableAmount : -Infinity,
            render: (a: Assignment) => (
              <MarginText
                margin={a.billableAmount > 0 ? (a.billableAmount - a.cost) / a.billableAmount : null}
              />
            ),
          },
        ]
      : []),
    {
      key: 'billRate',
      header: 'Bill rate /h',
      align: 'right',
      render: (a) => (
        <RateCell
          label={`Bill rate for ${a.person.name}`}
          value={a.billRate}
          placeholder={a.resolvedBillRate.toFixed(0)}
          disabled={updateAssignment.isPending}
          onSave={(billRate) => act(() => updateAssignment.mutateAsync({ assignmentId: a.id, billRate }))}
        />
      ),
    },
    {
      key: 'costRate',
      header: 'Cost rate /h',
      align: 'right',
      render: (a) => (
        <RateCell
          label={`Cost rate for ${a.person.name}`}
          value={a.costRate}
          placeholder={a.resolvedCostRate != null ? a.resolvedCostRate.toFixed(0) : 'flat'}
          disabled={updateAssignment.isPending}
          onSave={(costRate) => act(() => updateAssignment.mutateAsync({ assignmentId: a.id, costRate }))}
        />
      ),
    },
    {
      key: 'remove',
      header: '',
      align: 'right',
      className: 'w-12',
      render: (a) => (
        <button
          type="button"
          aria-label={`Remove ${a.person.name} from agreement`}
          className="rounded-lg p-1.5 text-grey-400 transition-colors hover:bg-status-late-bg hover:text-status-late"
          onClick={(e) => {
            e.stopPropagation();
            act(() => removeAssignment.mutateAsync(a.id));
          }}
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <Select
          label="Add person"
          selectSize="sm"
          className="w-64"
          placeholder="Pick a teammate…"
          options={detail.availablePeople.map((p) => ({
            value: p.id,
            label: `${p.name}${p.defaultBillRate != null ? ` — $${p.defaultBillRate.toFixed(0)}/h default` : ''}`,
          }))}
          value={personId}
          onChange={(e) => setPersonId(e.target.value)}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={!personId}
          loading={addAssignment.isPending}
          onClick={() =>
            act(async () => {
              await addAssignment.mutateAsync({ personId });
              setPersonId('');
            })
          }
        >
          <UserPlus className="h-4 w-4" aria-hidden />
          Add to agreement
        </Button>
        <p className="basis-full text-caption text-grey-500">
          Rate overrides beat the agreement rate and person defaults. A dashed field is inherited;
          clear a field to fall back.
        </p>
      </div>
      {error && (
        <p className="rounded-lg bg-status-late-bg px-3 py-2 text-body-sm text-status-late">{error}</p>
      )}
      <Card>
        <DataTable
          columns={columns}
          data={detail.assignments}
          rowKey={(a) => a.id}
          defaultSort={{ key: 'hours', dir: 'desc' }}
          empty={
            <EmptyState
              icon={<UserPlus />}
              title="Nobody is assigned yet"
              description="Add teammates so their time lands on this agreement with the right rates."
            />
          }
          footer={
            detail.assignments.length > 0 ? (
              <tr>
                <td className="px-4 py-2.5 text-caption uppercase tracking-wider text-grey-500">
                  {detail.assignments.length}{' '}
                  {detail.assignments.length === 1 ? 'person' : 'people'}
                  {detail.metrics.expenses > 0 && (
                    <span className="normal-case tracking-normal"> · time cost only</span>
                  )}
                </td>
                <td className="num-cell px-4 py-2.5">
                  {formatHours(totals.hours, { fixed: true })}
                </td>
                {hourly && (
                  <td className="num-cell px-4 py-2.5">
                    {formatMoney(totals.billable, { whole: true })}
                  </td>
                )}
                <td className="num-cell px-4 py-2.5">
                  {formatMoney(totals.cost, { whole: true })}
                </td>
                {hourly && (
                  <td className="num-cell px-4 py-2.5">
                    <MarginText
                      margin={
                        totals.billable > 0
                          ? (totals.billable - totals.cost) / totals.billable
                          : null
                      }
                    />
                  </td>
                )}
                <td />
                <td />
                <td />
              </tr>
            ) : undefined
          }
        />
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tasks tab (attached org tasks with per-agreement billable/rate overrides)
// ---------------------------------------------------------------------------

const BILLABLE_OVERRIDE_OPTIONS = (row: AgreementTaskRow) => [
  {
    value: 'default',
    label: `Default (${row.task.defaultBillable ? 'billable' : 'non-billable'})`,
  },
  { value: 'yes', label: 'Billable' },
  { value: 'no', label: 'Non-billable' },
];

/** Relative gap between configured and effective rate that earns the amber flag. */
const RATE_DIVERGENCE = 0.1;

const RATE_SOURCE_LABEL: Record<AgreementTaskRow['rateSource'], string> = {
  OVERRIDE: 'agreement override',
  TASK_DEFAULT: 'task default',
  PROJECT: 'agreement rate',
  NONE: 'no rate set',
};

function TasksTab({ detail }: { detail: AgreementDetail }) {
  const agreementId = detail.agreement.id;
  const attach = useAttachTask(agreementId);
  const updateAgreementTask = useUpdateAgreementTask(agreementId);
  const detach = useDetachTask(agreementId);
  const updateTask = useUpdateTask();
  const [taskId, setTaskId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<AgreementTaskRow | null>(null);
  const attachStandard = useAttachStandardTasks(detail.agreement.id);

  const act = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Update failed — try again.');
    }
  };

  const fallbackRate = (row: AgreementTaskRow): string => {
    // A non-billable task never bills, so showing a fallback rate would mislead.
    if (!row.resolvedBillable) return 'n/a';
    const rate = row.task.defaultRate ?? detail.agreement.hourlyRate;
    return rate != null ? rate.toFixed(0) : 'none';
  };

  const columns: Array<DataTableColumn<AgreementTaskRow>> = [
    {
      key: 'task',
      header: 'Task',
      sortable: true,
      sortValue: (r) => r.task.name,
      render: (r) => (
        <div className="flex min-w-[9rem] items-center gap-2">
          <span className="font-semibold text-secondary">{r.task.name}</span>
          {!r.task.isActive && <StatusPill status="neutral" label="Archived" dot={false} />}
        </div>
      ),
    },
    {
      key: 'hours',
      header: 'Hours',
      align: 'right',
      sortable: true,
      sortValue: (r) => r.hours,
      render: (r) =>
        r.hours > 0 ? formatHours(r.hours, { fixed: true }) : <span className="text-grey-400">—</span>,
    },
    {
      key: 'billableAmount',
      header: 'Billable value',
      align: 'right',
      sortable: true,
      sortValue: (r) => r.billableAmount,
      render: (r) =>
        r.billableAmount > 0 ? (
          formatMoney(r.billableAmount, { whole: true })
        ) : (
          <span className="text-grey-400">—</span>
        ),
    },
    {
      // The rate the tracked money above ACTUALLY resolved at. Without this the
      // configured rate sits next to arithmetic that used a different number.
      key: 'effectiveRate',
      header: 'Effective /h',
      align: 'right',
      sortable: true,
      sortValue: (r) => r.effectiveRate ?? -1,
      render: (r) => {
        if (r.effectiveRate == null) return <span className="text-grey-400">—</span>;
        // An exception marker that fires on every row is not a warning, it is
        // wallpaper. Historic entries legitimately carry their own snapshot
        // rate, so a couple of dollars' drift is normal; only a gap big enough
        // to change a conversation with the client is flagged.
        const diverges =
          r.resolvedRate != null &&
          r.resolvedRate > 0 &&
          Math.abs(r.effectiveRate - r.resolvedRate) / r.resolvedRate >= RATE_DIVERGENCE;
        return (
          <span
            className={diverges ? 'inline-flex items-center gap-1 text-status-partial' : undefined}
            title={
              diverges
                ? `Tracked time billed at ${formatMoney(r.effectiveRate)}/h — ${Math.round((Math.abs(r.effectiveRate - (r.resolvedRate ?? 0)) / (r.resolvedRate || 1)) * 100)}% off the ${RATE_SOURCE_LABEL[r.rateSource]} of ${formatMoney(r.resolvedRate ?? 0)}/h configured here. Entry snapshots and per-person rates outrank task rates.`
                : r.resolvedRate != null
                  ? `Within ${Math.round(RATE_DIVERGENCE * 100)}% of the configured ${RATE_SOURCE_LABEL[r.rateSource]} (${formatMoney(r.resolvedRate)}/h)`
                  : 'No rate configured for new time on this task'
            }
          >
            {diverges && <TriangleAlert className="h-3.5 w-3.5 shrink-0" aria-hidden />}
            <span className="num">{formatMoney(r.effectiveRate, { whole: true })}</span>
          </span>
        );
      },
    },
    {
      key: 'billable',
      header: 'Billable?',
      // w-48 clipped "Default (non-billable)" under the chevron.
      className: 'w-56',
      render: (r) => (
        <Select
          aria-label={`Billable setting for ${r.task.name}`}
          selectSize="sm"
          options={BILLABLE_OVERRIDE_OPTIONS(r)}
          value={r.billable === null ? 'default' : r.billable ? 'yes' : 'no'}
          disabled={updateAgreementTask.isPending}
          onChange={(e) =>
            act(() =>
              updateAgreementTask.mutateAsync({
                agreementTaskId: r.id,
                billable: e.target.value === 'default' ? null : e.target.value === 'yes',
              }),
            )
          }
        />
      ),
    },
    {
      key: 'rate',
      header: 'Rate /h (new time)',
      align: 'right',
      render: (r) => (
        <RateCell
          label={`Rate for ${r.task.name} on this agreement`}
          value={r.rate}
          placeholder={fallbackRate(r)}
          disabled={updateAgreementTask.isPending}
          onSave={(rate) => act(() => updateAgreementTask.mutateAsync({ agreementTaskId: r.id, rate }))}
        />
      ),
    },
    {
      key: 'remove',
      header: '',
      align: 'right',
      className: 'w-12',
      render: (r) =>
        r.entryCount > 0 ? (
          r.task.isActive ? (
            <button
              type="button"
              aria-label={`Archive ${r.task.name} (time is tracked against it)`}
              title={`${formatHours(r.hours)} h tracked — archive instead of removing`}
              className="rounded-lg p-1.5 text-grey-400 transition-colors hover:bg-grey-100 hover:text-secondary"
              onClick={(e) => {
                e.stopPropagation();
                setArchiveTarget(r);
              }}
            >
              <Archive className="h-4 w-4" aria-hidden />
            </button>
          ) : null
        ) : (
          <button
            type="button"
            aria-label={`Remove ${r.task.name} from this agreement`}
            className="rounded-lg p-1.5 text-grey-400 transition-colors hover:bg-status-late-bg hover:text-status-late"
            onClick={(e) => {
              e.stopPropagation();
              act(() => detach.mutateAsync(r.id));
            }}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <Select
          label="Attach a task"
          selectSize="sm"
          className="w-64"
          placeholder={
            detail.availableTasks.length === 0 ? 'All org tasks attached' : 'Pick a task…'
          }
          // In process order (standard tasks first), as the server sends them.
          // The rate is the client's, set on the agreement — not a per-task label.
          options={detail.availableTasks.map((t) => ({
            value: t.id,
            label: `${t.name}${t.isStandard ? ' — standard' : t.defaultBillable ? '' : ' — non-billable'}`,
          }))}
          value={taskId}
          disabled={detail.availableTasks.length === 0}
          onChange={(e) => setTaskId(e.target.value)}
        />
        <Button
          size="sm"
          variant="outline"
          disabled={!taskId}
          loading={attach.isPending}
          onClick={() =>
            act(async () => {
              await attach.mutateAsync({ taskId });
              setTaskId('');
            })
          }
        >
          <ListPlus className="h-4 w-4" aria-hidden />
          Attach
        </Button>
        {detail.missingStandardTasks.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            loading={attachStandard.isPending}
            title={`Attach ${detail.missingStandardTasks.map((t) => t.name).join(', ')}`}
            onClick={() => act(async () => void (await attachStandard.mutateAsync()))}
          >
            <ListPlus className="h-4 w-4" aria-hidden />
            Add the standard tasks ({detail.missingStandardTasks.length})
          </Button>
        )}
        <Link to="/settings?tab=tasks" className={buttonVariants({ size: 'sm', variant: 'ghost' })}>
          <ListChecks className="h-4 w-4" aria-hidden />
          Manage tasks
        </Link>
        <p className="basis-full text-caption text-grey-500">
          <strong className="font-semibold text-grey-600">Rate /h</strong> prices new time (dashed =
          inherited). <strong className="font-semibold text-grey-600">Effective /h</strong> is what
          tracked time actually billed at — amber flags a gap over{' '}
          {Math.round(RATE_DIVERGENCE * 100)}%.
        </p>
      </div>
      {error && (
        <p className="rounded-lg bg-status-late-bg px-3 py-2 text-body-sm text-status-late">{error}</p>
      )}
      <Card>
        <DataTable
          columns={columns}
          data={detail.tasks}
          rowKey={(r) => r.id}
          // In process order (the server's), not by hours — the columns still sort.
          empty={
            <EmptyState
              icon={<ListChecks />}
              title="No tasks attached"
              description="Attach tasks from the org list so people can pick them when tracking time on this agreement."
            />
          }
        />
      </Card>
      <ConfirmDialog
        open={archiveTarget != null}
        onOpenChange={(open) => !open && setArchiveTarget(null)}
        title={`Archive “${archiveTarget?.task.name ?? ''}”?`}
        description="Tracked time keeps its task, and the task stays attached everywhere — it just stops appearing in pickers across the organization. You can restore it from Settings → Tasks."
        confirmLabel="Archive task"
        onConfirm={() =>
          updateTask
            .mutateAsync({ taskId: archiveTarget!.task.id, isActive: false })
            .catch((e: unknown) => {
              setError(e instanceof ApiError ? e.message : 'Archive failed — try again.');
              throw e;
            })
        }
      />
    </div>
  );
}

function ExpensesTab({ expenses }: { expenses: AgreementExpense[] }) {
  const total = expenses.reduce((s, e) => s + e.amount, 0);
  const columns: Array<DataTableColumn<AgreementExpense>> = [
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      sortValue: (e) => e.date,
      render: (e) => <span className="num whitespace-nowrap">{formatDate(e.date)}</span>,
    },
    { key: 'vendor', header: 'Vendor', sortable: true, sortValue: (e) => e.vendor ?? '', render: (e) => e.vendor ?? '—' },
    {
      key: 'account',
      header: 'Account',
      render: (e) => (
        <span className="text-grey-600">
          {e.account.code ? `${e.account.code} · ` : ''}
          {e.account.name}
        </span>
      ),
    },
    {
      key: 'billable',
      header: 'Billable',
      render: (e) =>
        e.billable ? <StatusPill status="open" label="Billable" /> : <span className="text-grey-400">—</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      sortable: true,
      sortValue: (e) => e.amount,
      render: (e) => formatMoney(e.amount),
    },
  ];
  return (
    <Card>
      <DataTable
        columns={columns}
        data={expenses}
        rowKey={(e) => e.id}
        defaultSort={{ key: 'date', dir: 'desc' }}
        empty={
          <EmptyState
            icon={<Receipt />}
            title="No expenses on this agreement"
            description="Billable and non-billable expenses tagged to this agreement appear here and count toward cost."
          />
        }
        footer={
          expenses.length > 0 ? (
            <tr>
              <td className="px-4 py-2.5 text-caption uppercase tracking-wider text-grey-500" colSpan={4}>
                Total
              </td>
              <td className="num-cell px-4 py-2.5">{formatMoney(total)}</td>
            </tr>
          ) : undefined
        }
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Overview tab (chart + per-person breakdown)
// ---------------------------------------------------------------------------

/**
 * "Who is burning it" — the top few people by hours, beside the burn curve.
 *
 * A narrow column is the wrong shape for a five-column table, so this is a
 * list: share-of-hours bar, the money on both sides, and the per-person margin
 * the numbers were always one subtraction away from. The full sortable table
 * lives on the Team tab.
 */
function TopPeople({ detail }: { detail: AgreementDetail }) {
  const rows = detail.byPerson;
  const totalHours = rows.reduce((s, r) => s + r.hours, 0);
  const top = rows.slice(0, 5);
  const rest = rows.length - top.length;
  const showMargin = detail.metrics.basis === 'HOURLY';

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<Clock />}
        title="Nobody has tracked time"
        description="Per-person hours and cost appear once entries land on this agreement."
      />
    );
  }

  return (
    <div className="px-4 pb-4">
      <ul className="space-y-3">
        {top.map((r) => {
          const margin = r.billableAmount > 0 ? (r.billableAmount - r.cost) / r.billableAmount : null;
          return (
            <li key={r.person.id}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate text-body-sm font-semibold text-secondary">
                  {r.person.name}
                </span>
                <span className="num shrink-0 text-body-sm text-grey-600">
                  {formatHours(r.hours, { unit: true })}
                </span>
              </div>
              <ProgressBar
                value={totalHours > 0 ? r.hours / totalHours : 0}
                color="accent"
                size="sm"
                className="mt-1"
                aria-label={`${r.person.name}: ${formatHours(r.hours)} of ${formatHours(totalHours)} hours`}
              />
              <div className="mt-1 flex items-baseline justify-between gap-2 text-caption">
                <span className="num truncate text-grey-500">
                  {/* "$0 billable" on an internal agreement is noise, not a fact —
                      and on a fixed-bid one it is worse than noise: that money
                      will never be charged, because the price was agreed. */}
                  {showMargin &&
                    r.billableAmount > 0 &&
                    `${formatMoney(r.billableAmount, { whole: true })} billable · `}
                  {formatMoney(r.cost, { whole: true })} cost
                </span>
                {showMargin && (
                  <span className="shrink-0">
                    <MarginText margin={margin} />
                  </span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {rest > 0 && (
        <p className="mt-3 text-caption text-grey-500">
          +{rest} more on the <span className="font-semibold text-grey-600">Team</span> tab.
        </p>
      )}
    </div>
  );
}

const TASK_COST_TITLE =
  'Delivery cost of this task’s time — each person’s cost rate, with salaried people allocated across the hours they logged that month. TIME COST ONLY: agreement expenses are booked to the agreement, not to a task, so this column can total less than the Cost KPI above.';

function TaskFigure({ label, value, title }: { label: string; value: string; title?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2" title={title}>
      <dt className="shrink-0 text-caption text-grey-500">{label}</dt>
      <dd className="num truncate text-body-sm font-semibold text-secondary">{value}</dd>
    </div>
  );
}

function OverviewTab({ detail }: { detail: AgreementDetail }) {
  // A non-billable agreement has no bill rates and no billable value, so three
  // columns of "0.00" and "$0" under a heading that promises "resolved bill
  // rates" is copy written for a different agreement. Internal work reports where
  // the hours went and what share each task took.
  const nonBillable = detail.metrics.basis === 'NON_BILLABLE';
  /**
   * Hours × rate is REVENUE only on an hourly agreement. On a fee or fixed-bid
   * engagement the price was agreed once, for the whole contract, so a
   * per-task "billable value" and a per-task margin struck against it are both
   * fiction — and they sat one card away from a margin struck on the contract.
   * Those agreements get the same hours / share / cost table internal work gets:
   * where the scope went and what it cost.
   */
  const costOnly = detail.metrics.basis !== 'HOURLY';
  const taskHoursTotal = detail.byTask.reduce((s, r) => s + r.hours, 0);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const byTaskColumns: Array<DataTableColumn<TaskBreakdown>> = [
    {
      key: 'task',
      header: 'Task',
      sortable: true,
      sortValue: (r) => r.name,
      render: (r) => (
        <span className={r.taskId ? 'font-semibold text-secondary' : 'text-grey-500'}>{r.name}</span>
      ),
    },
    {
      key: 'hours',
      header: 'Hours',
      align: 'right',
      sortable: true,
      sortValue: (r) => r.hours,
      render: (r) => formatHours(r.hours, { fixed: true }),
    },
    ...(costOnly
      ? [
          {
            key: 'share',
            header: 'Share of hours',
            align: 'right' as const,
            sortable: true,
            sortValue: (r: TaskBreakdown) => r.hours,
            render: (r: TaskBreakdown) =>
              taskHoursTotal > 0 ? (
                formatPercent(r.hours / taskHoursTotal, { decimals: 1, fixed: true })
              ) : (
                <span className="text-grey-400">—</span>
              ),
          },
          {
            key: 'cost',
            header: <span title={TASK_COST_TITLE}>Cost</span>,
            align: 'right' as const,
            sortable: true,
            sortValue: (r: TaskBreakdown) => r.cost,
            render: (r: TaskBreakdown) => formatMoney(r.cost, { whole: true }),
          },
          {
            key: 'entries',
            header: 'Entries',
            align: 'right' as const,
            sortable: true,
            sortValue: (r: TaskBreakdown) => r.entryCount,
            render: (r: TaskBreakdown) => r.entryCount,
          },
        ]
      : [
          {
            key: 'billableHours',
            header: 'Billable h',
            align: 'right' as const,
            sortable: true,
            sortValue: (r: TaskBreakdown) => r.billableHours,
            render: (r: TaskBreakdown) => formatHours(r.billableHours, { fixed: true }),
          },
          {
            key: 'billable',
            header: 'Billable',
            align: 'right' as const,
            sortable: true,
            sortValue: (r: TaskBreakdown) => r.billableAmount,
            render: (r: TaskBreakdown) => formatMoney(r.billableAmount, { whole: true }),
          },
          // COST and MARGIN per task. Without them this card reported hours and
          // billable value — exactly what Harvest reports — in the one place you
          // would diagnose WHICH workstream is eating the budget. QA at 78%
          // margin and Development at 41% is the whole answer, and it was one
          // subtraction away the entire time.
          {
            key: 'cost',
            header: <span title={TASK_COST_TITLE}>Cost</span>,
            align: 'right' as const,
            sortable: true,
            sortValue: (r: TaskBreakdown) => r.cost,
            render: (r: TaskBreakdown) => formatMoney(r.cost, { whole: true }),
          },
          {
            key: 'margin',
            header: 'Margin',
            align: 'right' as const,
            sortable: true,
            // Non-billable tasks print "—"; blanks sink in both directions.
            sortValue: (r: TaskBreakdown) => r.margin,
            render: (r: TaskBreakdown) => <MarginText margin={r.margin} />,
          },
        ]),
  ];

  // The money columns are printed in whole dollars, so the footer sums the
  // ROUNDED figures — otherwise $376,574.40 + $96,191.20 prints as a column of
  // 376,574 and 96,191 under a total of 472,766, and a reader who adds up the
  // column they can see is told they are a dollar wrong.
  const taskTotals = detail.byTask.reduce(
    (a, r) => ({
      hours: a.hours + r.hours,
      billableHours: a.billableHours + r.billableHours,
      billableAmount: a.billableAmount + Math.round(r.billableAmount),
      cost: a.cost + Math.round(r.cost),
      entryCount: a.entryCount + r.entryCount,
    }),
    { hours: 0, billableHours: 0, billableAmount: 0, cost: 0, entryCount: 0 },
  );
  const taskTotalMargin =
    taskTotals.billableAmount > 0
      ? (taskTotals.billableAmount - taskTotals.cost) / taskTotals.billableAmount
      : null;

  return (
    <div className="space-y-4">
      {/* Prepaid hours lead when the agreement has any: purchased · burnt ·
          available is the question a support agreement is opened to answer. */}
      <PrepaidHoursCard agreementId={detail.agreement.id} clientId={detail.agreement.client.id} />
      {/* The curve and the people burning it share the first screen: at
          1440×900 nothing here needs a scroll to be seen. */}
      <CardRow cols="3-lg">
        <Card padding="md" className="lg:col-span-2">
          <BurnChart detail={detail} />
        </Card>
        {/* `natural` — "Top 2 by hours" is two rows; stretched to the chart
            beside it, it would print a band of dead white three times its own
            content. The BRIEF's own escape: past what a body can use, the extra
            height belongs to the tall neighbour and the short card simply ends.
            (This was `lg:self-start`, the same idea spelled at the call site.) */}
        <Card fill="natural">
          <CardHeader
            title="Time by person"
            subtitle={`Top ${Math.min(5, detail.byPerson.length) || ''} by hours${
              detail.metrics.basis === 'HOURLY' ? ', with each person’s margin' : ''
            }`}
          />
          <TopPeople detail={detail} />
        </Card>
      </CardRow>
      <Card>
        <CardHeader
          title="Time by task"
          subtitle={
            nonBillable
              ? 'Where the hours go and what each workstream costs — this agreement is non-billable, so none of it carries a rate'
              : costOnly
                ? 'Where the scope goes and what each workstream costs — the price is agreed for the whole contract, so no task carries revenue of its own'
                : 'Which workstream earns and which one costs — billable value at resolved rates against delivery cost'
          }
        />
        {/* Below `sm` this was a table: "BILLABLE H" wrapped onto two lines and
            BILLABLE $ was clipped clean off the right edge with no scroll cue —
            a money column you cannot see or reach. Six columns cannot survive
            390px, so the phone gets the same six figures as stacked rows. */}
        <div className="sm:hidden">
          {detail.byTask.length === 0 ? (
            <EmptyState
              icon={<ListChecks />}
              title="No time tracked yet"
              description="Per-task totals appear once time entries land on this agreement."
            />
          ) : (
            <ul className="divide-y divide-grey-100">
              {detail.byTask.map((r) => (
                <li key={r.taskId ?? 'none'}>
                  <Link
                    to={timeTabSearch(searchParams, r.taskId)}
                    aria-label={`Open the ${formatHours(r.hours, { unit: true })} on ${r.name}`}
                    className="block px-4 py-3 no-underline outline-none hover:bg-grey-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/50"
                  >
                  <div className="flex items-baseline justify-between gap-3">
                    <span
                      className={cn(
                        'truncate text-body-sm font-semibold',
                        r.taskId ? 'text-secondary' : 'text-grey-500',
                      )}
                    >
                      {r.name}
                    </span>
                    <span className="num shrink-0 text-body-sm font-semibold text-secondary">
                      {formatHours(r.hours, { unit: true })}
                    </span>
                  </div>
                  <dl className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1">
                    {costOnly ? (
                      <TaskFigure
                        label="Share"
                        value={
                          taskHoursTotal > 0
                            ? formatPercent(r.hours / taskHoursTotal, { decimals: 1, fixed: true })
                            : '—'
                        }
                      />
                    ) : (
                      <TaskFigure
                        label="Billable h"
                        value={formatHours(r.billableHours, { fixed: true })}
                      />
                    )}
                    {!costOnly && (
                      <TaskFigure
                        label="Billable"
                        value={formatMoney(r.billableAmount, { whole: true })}
                      />
                    )}
                    <TaskFigure
                      label="Cost"
                      value={formatMoney(r.cost, { whole: true })}
                      title={TASK_COST_TITLE}
                    />
                    {costOnly ? (
                      <TaskFigure label="Entries" value={String(r.entryCount)} />
                    ) : (
                      <div className="flex items-baseline justify-between gap-2">
                        <dt className="text-caption text-grey-500">Margin</dt>
                        <dd className="text-body-sm">
                          <MarginText margin={r.margin} />
                        </dd>
                      </div>
                    )}
                  </dl>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DataTable
          className="hidden sm:block"
          columns={byTaskColumns}
          data={detail.byTask}
          rowKey={(r) => r.taskId ?? 'none'}
          // Each figure opens the entries behind it, on the Time tab.
          rowHref={(r) => timeTabSearch(searchParams, r.taskId)}
          onRowClick={(r) => navigate(timeTabSearch(searchParams, r.taskId))}
          rowLabel={(r) => `Open the ${formatHours(r.hours, { unit: true })} on ${r.name}`}
          defaultSort={{ key: 'hours', dir: 'desc' }}
          empty={
            <EmptyState
              icon={<ListChecks />}
              title="No time tracked yet"
              description="Per-task totals appear once time entries land on this agreement."
            />
          }
          footer={
            detail.byTask.length > 0 ? (
              <tr>
                <td className="px-4 py-2.5 text-caption uppercase tracking-wider text-grey-500">
                  All tasks
                </td>
                <td className="num-cell px-4 py-2.5">
                  {formatHours(taskTotals.hours, { fixed: true })}
                </td>
                {costOnly ? (
                  <>
                    <td className="num-cell px-4 py-2.5">100.0%</td>
                    <td className="num-cell px-4 py-2.5">
                      {formatMoney(taskTotals.cost, { whole: true })}
                    </td>
                    <td className="num-cell px-4 py-2.5">{taskTotals.entryCount}</td>
                  </>
                ) : (
                  <>
                    <td className="num-cell px-4 py-2.5">
                      {formatHours(taskTotals.billableHours, { fixed: true })}
                    </td>
                    <td className="num-cell px-4 py-2.5">
                      {formatMoney(taskTotals.billableAmount, { whole: true })}
                    </td>
                    <td className="num-cell px-4 py-2.5">
                      {formatMoney(taskTotals.cost, { whole: true })}
                    </td>
                    <td className="num-cell px-4 py-2.5">
                      <MarginText margin={taskTotalMargin} />
                    </td>
                  </>
                )}
              </tr>
            ) : undefined
          }
        />
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function DetailSkeleton() {
  return (
    <div>
      <Skeleton className="h-4 w-24" />
      <div className="mt-3 flex items-center gap-3">
        <Skeleton className="h-8 w-72" />
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>
      <Skeleton className="mt-2 h-4 w-56" />
      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} padding="md">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="mt-3 h-7 w-24" />
          </Card>
        ))}
      </div>
      <Card className="mt-6">
        <SkeletonTable rows={7} />
      </Card>
    </div>
  );
}

const TAB_VALUES = ['overview', 'team', 'tasks', 'time', 'expenses'] as const;

/** Hours or dollars, whichever the burn is denominated in. */
const burnUnit = (burn: NonNullable<AgreementDetail['metrics']['burn']>, v: number) =>
  burn.basis === 'HOURS' ? `${formatHours(v)} h` : formatMoney(v, { whole: true });

/** One glyph per action kind, so the button reads before it is read. */
function ActionIcon({ kind }: { kind: AgreementActionKind }) {
  const Icon = kind === 'fee' ? DollarSign : kind === 'rates' ? Users : Target;
  return <Icon className="h-4 w-4" aria-hidden />;
}

interface Kpi {
  label: string;
  /** Glyph for the tile, from the app-wide vocabulary in `ui/stat-icons`. */
  kind: StatKind;
  value: ReactNode;
  hint?: ReactNode;
  hintLines?: 1 | 2;
  footer?: ReactNode;
}

export function AgreementDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const query = useAgreementDetail(id);
  const reports = useAgreementReports(id);
  const archive = useArchiveAgreement();
  const [editOpen, setEditOpen] = useState(false);
  const [sellOpen, setSellOpen] = useState(false);
  // Opened from a client's Agreements tab, every exit stays inside that client
  // — Back, and the invoice this page raises.
  const from = useReturnOrigin();
  // Tab lives in the URL so a tab is linkable and survives a refresh.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab = TAB_VALUES.includes(tabParam as (typeof TAB_VALUES)[number])
    ? (tabParam as string)
    : 'overview';

  const detail = query.data;
  const notFound = query.error instanceof ApiError && query.error.status === 404;

  /**
   * The KPI row is basis-driven: an hourly agreement reports billable value and
   * what is still unbilled; a fee agreement reports its contract, what has been
   * invoiced against it, and what it cost to deliver. Mixing the two is what
   * produced "$113,505 unbilled" on a $250/month hosting plan.
   */
  const cards = useMemo((): Array<Kpi | null> | null => {
    if (!detail) return null;
    const m = detail.metrics;
    const { agreement } = detail;
    const hoursCard = {
      label: 'Hours',
      kind: 'hours' as const,
      value: formatHours(m.hours),
      hint: `${formatHours(m.billableHours)} billable h`,
    };
    // Salaried cost is absorbed across each person's total hours that month
    // ORG-WIDE, so this agreement's cost genuinely moves when they log time
    // elsewhere. Silently-drifting cost is how a finance user stops trusting a
    // screen; say why it moves.
    const salaried = detail.byPerson.some((p) => p.person.costModel === 'FLAT_MONTHLY');
    const costCard = {
      label: 'Cost',
      kind: 'cost' as const,
      value: formatMoney(m.cost, { whole: true }),
      // One short line that fits a 200px tile. Every hint on this row is one
      // line so the five cards are the same height — the row used to leave a
      // blank lower third in Hours and Margin because Cost wrapped to two.
      hint: (
        <span
          title={
            salaried
              ? 'Salaried people are costed by spreading their monthly salary across every hour they logged that month, across all agreements. This agreement’s cost therefore moves as they track time elsewhere in the same month.'
              : undefined
          }
        >
          {m.expenses > 0
            ? `${formatMoney(m.expenses, { whole: true })} expenses`
            : 'all time cost'}
          {salaried ? ' · salaried' : ''}
        </span>
      ),
    };
    // The margin names the basis its denominator came from. "on $48,000
    // revenue" beside a burn bar measuring cost against the same $48,000 read
    // as two different numbers; "on $48,000 contract value" reads as one.
    const marginCard = {
      label: 'Margin',
      kind: 'margin' as const,
      value: <MarginText margin={m.margin} size="heading" />,
      hint:
        m.margin == null
          ? 'no revenue recognised yet'
          : `on ${formatMoney(m.revenue, { whole: true })} ${REVENUE_SOURCE_NOUN[m.revenueSource]}`,
    };

    if (m.basis === 'HOURLY') {
      return [
        hoursCard,
        {
          label: 'Billable value',
          kind: 'billable' as const,
          // The unbilled figure is no longer grey subtitle text — it is the
          // hero's primary button. Repeating it here would be the third copy
          // of a number the reader is meant to ACT on.
          value: formatMoney(m.billableAmount, { whole: true }),
          hint: `${formatMoney(m.invoiced, { whole: true })} invoiced`,
        },
        costCard,
        marginCard,
        null, // budget card is appended by the caller
      ];
    }
    if (m.basis === 'NON_BILLABLE') {
      return [
        hoursCard,
        costCard,
        {
          label: 'Expenses',
          kind: 'expenses' as const,
          value: formatMoney(m.expenses, { whole: true }),
          hint: 'booked to this agreement',
        },
        {
          label: 'Cost / hour',
          kind: 'cost' as const,
          value: m.hours > 0 ? formatMoney(m.cost / m.hours) : '—',
          hint: 'internal overhead',
        },
        null,
      ];
    }
    // Fee agreements: the fee itself is NOT its own tile any more — the burn tile
    // in slot 5 prints "$1,207 cost of $250 fee · Aug 2026" plus the lifetime
    // line, which says everything a "Monthly fee $250" tile said and adds the
    // consumption. Spending a fifth of the row restating the contract is what
    // pushed the dual-burn treatment off the page in the first place.
    const monthly = m.basis === 'MONTHLY_FEE';
    return [
      hoursCard,
      {
        label: 'Invoiced',
        kind: 'invoice' as const,
        value: formatMoney(m.invoiced, { whole: true }),
        // "$5,000" means ONE thing on this page: the fee accrued to date. The
        // tile beside this one burns cost against the same figure and used to
        // call it something else, so the reader met one number wearing two
        // names side by side.
        hint:
          m.contracted != null && m.contracted > 0 ? (
            <span
              title={
                monthly
                  ? `${formatMoney(agreement.budgetAmount ?? 0, { whole: true })}/month × ${
                      m.feeMonths
                    } months live = ${formatMoney(m.contracted, {
                      whole: true,
                    })} of fee accrued to date. The Fee headroom tile burns delivery cost against the same figure.`
                  : `Invoiced against the ${formatMoney(m.contracted, { whole: true })} fixed fee.`
              }
            >
              {Math.round((m.invoiced / m.contracted) * 100)}% of{' '}
              {formatMoney(m.contracted, { whole: true })}
              {monthly ? ` accrued` : ' fee'}
            </span>
          ) : (
            'to date'
          ),
      },
      costCard,
      marginCard,
      null, // budget/fee-headroom card is appended by the caller
    ];
  }, [detail]);

  /**
   * The budget tile carries its own burn.
   *
   * There used to be a full-width bar band under this row saying "147% · 1,465 h
   * tracked of 1,000 h" — the same fact as the number in this tile and the same
   * fact as the chart's budget line, three renderings of one thing across the
   * most valuable 900px on the page. The bar is now a sparkline inside the tile
   * that already owns the number, and the band is gone.
   */
  const budgetCard = useMemo((): Kpi | null => {
    if (!detail) return null;
    const burn = detail.metrics.burn;
    if (!burn) return null;
    const remaining = burn.budget - burn.used;
    const label =
      burn.basis === 'HOURS' ? (
        <span className={remaining < 0 ? 'text-status-late' : undefined}>
          {remaining < 0 ? '−' : ''}
          {formatHours(Math.abs(remaining))} h
        </span>
      ) : (
        <SignedMoney value={remaining} />
      );
    const period = burn.periodMonth ? formatMonthShort(burn.periodMonth) : null;
    return {
      // A period-scoped tile names its period in the LABEL, so the headline
      // figure is never read as the whole engagement.
      label:
        burn.basis === 'MONTHLY_AMOUNT'
          ? `Fee headroom · ${period ?? 'this month'}${isPartialBurn(burn) ? ' so far' : ''}`
          : 'Budget left',
      kind: 'budget',
      value: label,
      /**
       * The caption is the HINT line, exactly like the other four tiles, and
       * the bar is a 4px rule under it. The bar used to sit in a footer block
       * with the caption beneath it, which made this tile a third taller than
       * its neighbours — that difference is where the row's "blank lower third"
       * came from. It also names its measure ("$1,207 **cost** of $250 fee"),
       * because the number beside it is not the same kind of thing.
       */
      hint: (
        <span className="num truncate" title={burnTitle(burn)}>
          {burnUnit(burn, burn.used)}
          {burn.measure === 'COST' ? ' cost' : ''} of {burnUnit(burn, burn.budget)}
          {burn.basis === 'MONTHLY_AMOUNT' ? ' fee' : ''}
          {/* "so far" only where there is room for it — the striped bar, the
              month in the label and the title sentence all say the same thing,
              and a clipped "…$250 fee s…" says nothing at all. */}
          {burn.periodClosed ? ' (final)' : ''}
        </span>
      ),
      hintLines: 1,
      footer: (
        <div title={burnTitle(burn)}>
          <ProgressBar
            value={burn.pct}
            size="sm"
            striped={isPartialBurn(burn)}
            aria-label={burnTitle(burn)}
          />
          {/* A period-scoped bar is ONE MONTH. The lifetime figure travels
              beside it everywhere else in the app; it travels with it here too,
              against the same "$5,000 fee accrued" the Invoiced tile names. */}
          {burn.lifetime && (
            <p className="num mt-1 text-caption text-grey-500">
              <span className="font-semibold text-grey-400">Life </span>
              {formatPercent(burn.lifetime.pct, { decimals: 0 })} ·{' '}
              {burnUnit(burn, burn.lifetime.used)} of{' '}
              {formatMoney(burn.lifetime.budget, { whole: true })}
            </p>
          )}
        </div>
      ),
    };
  }, [detail]);

  if (query.isLoading) return <DetailSkeleton />;

  if (notFound || (!query.isLoading && !detail)) {
    return (
      <div>
        {notFound ? (
          <Card>
            <EmptyState
              icon={<Archive />}
              title="Agreement not found"
              description="It may have been deleted, or it belongs to a different organization."
              action={
                <Button variant="outline" onClick={() => navigate('/agreements')}>
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  Back to agreements
                </Button>
              }
            />
          </Card>
        ) : (
          <ErrorState
            card
            message={query.error instanceof Error ? query.error.message : undefined}
            onRetry={() => query.refetch()}
          />
        )}
      </div>
    );
  }
  if (!detail || !cards) return null;

  const { agreement, metrics } = detail;
  const verdict = agreementVerdict(agreement, metrics, detail.byMonth);
  const action = agreementAction(agreement, metrics, detail.unbilled, detail.byMonth);
  const kpis: Kpi[] = cards.filter((c): c is Kpi => c != null);
  if (kpis.length < 5) {
    kpis.push(
      budgetCard ?? {
        label: 'Budget',
        kind: 'budget',
        value: <span className="text-grey-400">—</span>,
        hint: 'No budget set — no burn to track against.',
        hintLines: 2,
        footer: (
          <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
            <Target className="h-4 w-4" aria-hidden />
            Set a budget
          </Button>
        ),
      },
    );
  }

  return (
    <div>
      <BackLink
        to={from === 'client' ? `/clients/${agreement.client.id}?tab=agreements` : '/agreements'}
      >
        {from === 'client' ? agreement.client.name : 'Agreements'}
      </BackLink>

      <div className="mt-2 flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-heading-2 text-secondary">{agreement.name}</h1>
            <TypeBadge type={agreement.type} />
            {/* The verdict, next to the name, before any tile. Everything below
                is evidence; this is the answer. */}
            <VerdictChip tone={verdict.tone} label={verdict.label} title={verdict.reason} />
            {!agreement.isActive && <StatusPill status="neutral" label="Archived" dot={false} />}
          </div>
          <p className="mt-1 text-body-sm text-grey-500">
            {agreement.client.name}
            {agreement.code ? ` · ${agreement.code}` : ''}
            {agreement.startDate ? ` · ${formatDate(agreement.startDate)}` : ''}
            {agreement.endDate ? ` – ${formatDate(agreement.endDate)}` : agreement.startDate ? ' – ongoing' : ''}
            {` · ${BASIS_LABEL[metrics.basis]}`}
            {metrics.basis === 'HOURLY' && agreement.hourlyRate != null
              ? ` at ${formatMoney(agreement.hourlyRate, { whole: true })}/h`
              : ''}
            {metrics.basis === 'MONTHLY_FEE' && agreement.budgetAmount != null
              ? ` of ${formatMoney(agreement.budgetAmount, { whole: true })}/mo`
              : ''}
          </p>
          <p className="mt-1.5 max-w-2xl text-body-sm font-semibold text-secondary">
            {verdict.reason}
          </p>
          {/* Hub linkage lives with the agreement, not only in a settings tab. */}
          <AgreementHubLink
            agreementId={agreement.id}
            agreementName={agreement.name}
            hubProjectIds={agreement.hubProjectIds ?? []}
          />
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          {/* Harvest's best agreement-page affordance is the uninvoiced amount
              with one click to bill it. Ours had no path from agreement to
              invoice at all — and then, for a year, ONLY that: the worst
              agreement in the portfolio offered no next step because it had no
              unbilled money. The slot now follows the verdict (bill it, reprice
              it, look at the rates, set the budget) and is empty only when
              nothing is wrong. */}
          {action &&
            (action.to ? (
              <Link
                to={action.to}
                className={buttonVariants({ size: 'sm', className: 'w-full sm:w-auto' })}
                title={action.title}
              >
                <ActionIcon kind={action.kind} />
                {action.label}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            ) : (
              <Button
                size="sm"
                className="w-full sm:w-auto"
                title={action.title}
                onClick={() => setEditOpen(true)}
              >
                <ActionIcon kind={action.kind} />
                {action.label}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Button>
            ))}
          {/* Always offered, whatever the verdict says: a fee agreement has no
              unbilled hours to "bill", and until this the only way to invoice
              one was /invoices/new from scratch. The manual builder opens with
              this client chosen and one line seeded from the agreement — its
              name, its kind and its rate (Jesse, 2026-09-10). */}
          {agreement.isActive && agreement.type === 'SUPPORT' ? (
            /* A support agreement sells HOURS up front, never time spent
               (Jesse, 2026-09-10): the dialog asks how many, at the client's
               rate, and the hours land in the bank when the invoice is sent. */
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSellOpen(true)}
              title="Sell a block of support hours — a draft invoice, and the hours count once it is sent"
            >
              <Receipt className="h-4 w-4" aria-hidden />
              Sell hours
            </Button>
          ) : agreement.isActive ? (
            <Link
              to={withReturn(
                // A fixed bid is billed in installments; anything else opens
                // the hand-built invoice seeded from the agreement. The
                // agreement rides in the URL, so the other tabs keep it too.
                `/invoices/new?tab=${agreement.billBy === 'FIXED' ? 'installments' : 'manual'}&clientId=${encodeURIComponent(agreement.client.id)}&agreementId=${encodeURIComponent(agreement.id)}`,
                from,
              )}
              className={buttonVariants({ size: 'sm', variant: 'outline' })}
              title="Draft an invoice for this agreement — the client and a first line at its rate are filled in"
            >
              <Receipt className="h-4 w-4" aria-hidden />
              New invoice
            </Link>
          ) : null}
          {/* The client asked where the hours went — offered to whoever the
              server says may build and share one (owners/admins today). */}
          {reports.data?.canRun && (
            <Link
              to={`/agreements/${agreement.id}/report`}
              className={buttonVariants({ size: 'sm', variant: 'outline' })}
              title="A breakdown of this agreement's hours by task and by what the work was, to share with the client"
            >
              <PieChart className="h-4 w-4" aria-hidden />
              Hours report
            </Link>
          )}
          <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
            <Pencil className="h-4 w-4" aria-hidden />
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            loading={archive.isPending}
            onClick={() => archive.mutate({ id: agreement.id, archived: agreement.isActive })}
          >
            {agreement.isActive ? (
              <>
                <Archive className="h-4 w-4" aria-hidden />
                Archive
              </>
            ) : (
              <>
                <ArchiveRestore className="h-4 w-4" aria-hidden />
                Restore
              </>
            )}
          </Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        {kpis.map((c, i) => (
          <StatCard
            key={c.label}
            label={c.label}
            icon={<StatIcon kind={c.kind} />}
            value={c.value}
            hint={c.hint}
            // Two lines everywhere on this row: at 1440 the Cost hint clipped
            // to "incl. $869 expenses · sal…" and at 390 both Cost and Margin
            // clipped. A KPI card that ellipsises its own figure is not dense.
            hintLines={c.hintLines ?? 2}
            footer={c.footer}
            className={i === 4 ? 'col-span-2 md:col-span-1' : undefined}
          />
        ))}
      </div>

      <div className="mt-4">
        <Tabs
          // Narrow screens: let the tab strip scroll instead of wrapping labels.
          className="[&>[role=tablist]]:flex-nowrap [&>[role=tablist]]:overflow-x-auto [&_[role=tab]]:whitespace-nowrap"
          value={tab}
          onValueChange={(next) =>
            setSearchParams(
              (prev) => {
                const params = new URLSearchParams(prev);
                if (next === 'overview') params.delete('tab');
                else params.set('tab', next);
                return params;
              },
              { replace: true },
            )
          }
          items={[
            { value: 'overview', label: 'Overview', content: <OverviewTab detail={detail} /> },
            {
              value: 'team',
              label: `Team (${detail.assignments.length})`,
              content: <TeamTab detail={detail} />,
            },
            {
              value: 'tasks',
              label: `Tasks (${detail.tasks.length})`,
              content: <TasksTab detail={detail} />,
            },
            { value: 'time', label: 'Time', content: <AgreementTimeTab detail={detail} /> },
            {
              value: 'expenses',
              label: `Expenses (${detail.expenses.length})`,
              content: <ExpensesTab expenses={detail.expenses} />,
            },
          ]}
        />
      </div>

      <AgreementModal open={editOpen} onOpenChange={setEditOpen} agreement={agreement} />
      <SellHoursModal
        open={sellOpen}
        onOpenChange={setSellOpen}
        agreement={agreement}
        from={from}
      />
    </div>
  );
}
